<template>
  <dashboard-collapsible-card
    title="Lectures"
    :collapsed="collapsed"
    collapsed-icon="fas fa-eye text-primary-600"
    card-class="dashboard-overview-card dashboard-overview-card--reads h-full flex flex-col"
    header-class="dashboard-card-header"
    content-class="dashboard-reads-card__content"
    @toggle="$emit('toggle')"
  >
    <template #header-actions>
      <div class="flex flex-wrap gap-2">
        <dashboard-metric-chip label="Total lectures">
          <template #value>
            <span class="flex items-center gap-1 text-sm font-semibold text-primary-600">
              <span v-if="loadingReadTracking"><i class="fas fa-spinner fa-spin"></i></span>
              <span v-else>{{ formatNumber(totalReadsPeriod) }}</span>
            </span>
          </template>
        </dashboard-metric-chip>
        <dashboard-metric-chip label="Lectures articles">
          <template #value>
            <span class="flex items-center gap-1 text-sm font-semibold text-primary-600">
              <span v-if="loadingReadTracking"><i class="fas fa-spinner fa-spin"></i></span>
              <span v-else>{{ formatNumber(newsViewsPeriod) }}</span>
            </span>
          </template>
        </dashboard-metric-chip>
        <dashboard-metric-chip label="Lectures FAQ">
          <template #value>
            <span class="flex items-center gap-1 text-sm font-semibold text-primary-600">
              <span v-if="loadingReadTracking"><i class="fas fa-spinner fa-spin"></i></span>
              <span v-else>{{ formatNumber(faqViewsPeriod) }}</span>
            </span>
          </template>
        </dashboard-metric-chip>
      </div>
    </template>

    <template #collapsed-description>
      <p class="text-xs text-gray-600">
        Lectures: <strong class="text-gray-900">{{ formatNumber(totalReadsPeriod) }}</strong>
      </p>
    </template>

    <div class="dashboard-reads-card">
      <section class="dashboard-reads-card__chart" aria-label="Graphique des lectures">
        <div class="dashboard-reads-chart-panel">
          <dashboard-list-state
            :loading="loadingReadTracking"
            :has-data="hasReadSeries"
            class="h-full"
          >
            <template #default>
              <chart-connection-trend
                class="h-full w-full"
                :chart-data="readChartData"
                :show-legend="false"
              />
            </template>
            <template #loading>
              <div class="space-y-3">
                <div class="dashboard-skeleton h-40 rounded-lg"></div>
                <div class="dashboard-skeleton h-3 w-2/3 rounded-full"></div>
              </div>
            </template>
            <template #empty>
              <div class="dashboard-compact-empty h-full">Aucune lecture</div>
            </template>
          </dashboard-list-state>
        </div>
        <div class="dashboard-segmented-control dashboard-reads-segmented-control" aria-label="Donnée du graphique lectures">
          <button
            v-for="option in readChartOptions"
            :key="option.value"
            type="button"
            :class="{ 'is-active': readChartMode === option.value }"
            @click="readChartMode = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </section>

      <div class="dashboard-reads-card__tops">
        <section class="dashboard-top-list">
          <h4>Actualités - top 3</h4>
          <dashboard-list-state
            :loading="loadingTopNews"
            :has-data="topNewsThree.length"
          >
            <template #default>
              <ol class="dashboard-compact-list">
                <li
                  v-for="(article, index) in topNewsThree"
                  :key="article.uuid || article.title || index"
                >
                  <span>#{{ index + 1 }}</span>
                  <dashboard-tooltip-pill
                    variant="text"
                    :tooltip="article.title"
                    class="dashboard-compact-list__label"
                  >
                    {{ article.title }}
                  </dashboard-tooltip-pill>
                  <strong>{{ formatNumber(article.views) }}</strong>
                </li>
              </ol>
            </template>
            <template #loading>
              <div class="space-y-2">
                <div v-for="index in 3" :key="index" class="dashboard-skeleton h-8 rounded-lg"></div>
              </div>
            </template>
            <template #empty>
              <div class="dashboard-compact-empty">Aucune actualité</div>
            </template>
          </dashboard-list-state>
        </section>

        <section class="dashboard-top-list">
          <h4>Questions - top 3</h4>
          <dashboard-list-state
            :loading="loadingPopularities"
            :has-data="topFaqsThree.length"
          >
            <template #default>
              <ol class="dashboard-compact-list">
                <li
                  v-for="(faq, index) in topFaqsThree"
                  :key="faq.uuid || faq.title || index"
                >
                  <span>#{{ index + 1 }}</span>
                  <dashboard-tooltip-pill
                    variant="text"
                    :tooltip="faq.title"
                    class="dashboard-compact-list__label"
                  >
                    {{ faq.title }}
                  </dashboard-tooltip-pill>
                  <strong>{{ formatNumber(faq.views) }}</strong>
                </li>
              </ol>
            </template>
            <template #loading>
              <div class="space-y-2">
                <div v-for="index in 3" :key="index" class="dashboard-skeleton h-8 rounded-lg"></div>
              </div>
            </template>
            <template #empty>
              <div class="dashboard-compact-empty">Aucune question</div>
            </template>
          </dashboard-list-state>
        </section>
      </div>
    </div>
  </dashboard-collapsible-card>
</template>

<script>
import DashboardCollapsibleCard from '@/components/dashboard/DashboardCollapsibleCard.vue'
import DashboardListState from '@/components/dashboard/DashboardListState.vue'
import DashboardMetricChip from '@/components/dashboard/DashboardMetricChip.vue'
import DashboardTooltipPill from '@/components/dashboard/DashboardTooltipPill.vue'
import ChartConnectionTrend from '@/components/charts/ChartConnectionTrend.vue'

export default {
  name: 'DashboardReadsCard',
  components: {
    DashboardCollapsibleCard,
    DashboardListState,
    DashboardMetricChip,
    DashboardTooltipPill,
    ChartConnectionTrend
  },
  props: {
    collapsed: {
      type: Boolean,
      required: true
    },
    topNews: {
      type: Array,
      default: () => []
    },
    topFaqs: {
      type: Array,
      default: () => []
    },
    counters: {
      type: Object,
      required: true
    },
    dailySeries: {
      type: Array,
      default: () => []
    },
    readTotals: {
      type: Object,
      default: null
    },
    loadingReadTracking: {
      type: Boolean,
      default: false
    },
    loadingTopNews: {
      type: Boolean,
      default: false
    },
    loadingPopularities: {
      type: Boolean,
      default: false
    },
    formatNumber: {
      type: Function,
      required: true
    }
  },
  data () {
    return {
      readChartMode: 'allReads'
    }
  },
  emits: ['toggle'],
  computed: {
    hasReadTotals () {
      return this.readTotals && typeof this.readTotals === 'object'
    },
    newsViewsPeriod () {
      if (this.hasReadTotals) {
        const value = Number(this.readTotals.articleReads)
        return Number.isFinite(value) && value >= 0 ? value : 0
      }

      const period = this.counters && this.counters.period
      return period && typeof period.newsViews === 'number' ? period.newsViews : 0
    },
    faqViewsPeriod () {
      if (this.hasReadTotals) {
        const value = Number(this.readTotals.faqReads)
        return Number.isFinite(value) && value >= 0 ? value : 0
      }

      const period = this.counters && this.counters.period
      return period && typeof period.faqViews === 'number' ? period.faqViews : 0
    },
    totalReadsPeriod () {
      if (this.hasReadTotals) {
        const value = Number(this.readTotals.totalReads)
        return Number.isFinite(value) && value >= 0 ? value : this.newsViewsPeriod + this.faqViewsPeriod
      }

      return this.newsViewsPeriod + this.faqViewsPeriod
    },
    topNewsThree () {
      return (this.topNews || []).slice(0, 3)
    },
    topFaqsThree () {
      return (this.topFaqs || []).slice(0, 3)
    },
    normalizedDailySeries () {
      const rows = Array.isArray(this.dailySeries) ? this.dailySeries : []
      return rows.map(row => ({
        date: row?.date || '',
        allReads: Number(row?.allReads || 0),
        articleReads: Number(row?.articleReads || 0),
        faqReads: Number(row?.faqReads || 0)
      }))
    },
    hasReadSeries () {
      return this.normalizedDailySeries.some(row => row.allReads > 0 || row.articleReads > 0 || row.faqReads > 0)
    },
    readChartOptions () {
      return [
        { value: 'allReads', label: 'Lecture all' },
        { value: 'articleReads', label: 'Articles' },
        { value: 'faqReads', label: 'FAQ' }
      ]
    },
    readChartData () {
      const config = this.readChartConfig
      return {
        labels: this.normalizedDailySeries.map(item => this.formatDateLabel(item.date)),
        datasets: [
          {
            label: config.label,
            data: this.normalizedDailySeries.map(item => Number(item[config.key] || 0)),
            borderColor: config.color,
            backgroundColor: config.backgroundColor,
            borderWidth: this.normalizedDailySeries.length > 31 ? 0.75 : 1,
            pointBackgroundColor: config.color,
            pointRadius: this.normalizedDailySeries.length > 31 ? 0 : (this.normalizedDailySeries.length > 14 ? 1 : 1.5),
            pointHoverRadius: 2,
            pointHitRadius: 8,
            fill: false
          }
        ]
      }
    },
    readChartConfig () {
      if (this.readChartMode === 'articleReads') {
        return {
          key: 'articleReads',
          label: 'Lectures articles',
          color: '#7c3aed',
          backgroundColor: 'rgba(124, 58, 237, 0.12)'
        }
      }

      if (this.readChartMode === 'faqReads') {
        return {
          key: 'faqReads',
          label: 'Lectures FAQ',
          color: '#0f766e',
          backgroundColor: 'rgba(15, 118, 110, 0.12)'
        }
      }

      return {
        key: 'allReads',
        label: 'Lectures all',
        color: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.12)'
      }
    }
  },
  methods: {
    formatDateLabel (value) {
      if (!value || typeof value !== 'string' || value.length < 10) {
        return value || ''
      }

      return `${value.slice(8, 10)}/${value.slice(5, 7)}`
    }
  }
}
</script>
